import { getCustomerList } from "Api/common";
import { addCreditNote, editCreditNote, taxInvoiceDataApi, taxInvoiceDropdownApi, viewCreditNote } from "Api/creditNote";
import BaseButton from "Components/Base/BaseButton";
import BaseInput from "Components/Base/BaseInput";
import Loader from "Components/Base/BaseLoader";
import { BaseSelect } from "Components/Base/BaseSelect";
import { checkStatusCodeSuccess, errorHandler, NormalDate } from "Components/constants/Common";
import { CreditNoteLabel } from "Components/constants/CreditNote";
import { cancelLabel, submitLabel, updateLabel } from "Components/constants/Role";
import { TaxInvoiceLabel } from "Components/constants/taxInvoice";
import { InputPlaceHolder, SelectPlaceHolder, validationMessages } from "Components/constants/validation";
import { WorkOrderLabel } from "Components/constants/WorkOrder";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";

const CreditNoteForm = () => {
    const navigate = useNavigate();
    const { creditNoteId } = useParams();

    const [loader, setLoader] = useState(false);
    const [loading, setLoading] = useState(false);
    const [vendors, setVendors] = useState([]);
    const [taxInvoice, setTaxInvoice] = useState([]);
    const [taxInvoiceData, setTaxInvoiceData] = useState<any>(null);
    const [creditNoteData, setCreditNoteData] = useState<any>([]);

    const formInitialValue = {
        taxInvoiceId: creditNoteData ? creditNoteData?.tax_invoice_id : null,
        workOrderId: null,
        quotationId: null,        
        vendorName: creditNoteData ? creditNoteData?.customer_name : null,
        vendorId: creditNoteData ? creditNoteData?.customer_id : null,
        originalInvoiceNo: creditNoteData ? creditNoteData?.original_invoice_number : "",
        originalInvoiceDate: creditNoteData ? NormalDate(creditNoteData?.original_invoice_date) : '',
        creditNoteNo: creditNoteData ? creditNoteData?.credit_note_no : "",
        creditNoteDate: creditNoteData ? NormalDate(creditNoteData?.credit_note_date) : "",
        creditNoteAmt: creditNoteData ? creditNoteData?.credit_note_amount : "",
        eWayBill: creditNoteData ? creditNoteData?.e_way_bill : "",
        reason: creditNoteData ? creditNoteData?.reason : "",
    };

    const validationSchema = Yup.object({
        taxInvoiceId: Yup.string().required(validationMessages.required(TaxInvoiceLabel.TaxInvoiceNo)),
        vendorName: Yup.string().required(validationMessages.required(WorkOrderLabel.VendorName)),
        workOrderId: Yup.string().required(validationMessages.required(WorkOrderLabel.WorkOrderNo)),
        quotationId: Yup.string().required(validationMessages.required(WorkOrderLabel.QuoteNo)),
        vendorId: Yup.string().required(validationMessages.required(WorkOrderLabel.VendorId)),
        creditNoteDate: Yup.string().required(validationMessages.required(CreditNoteLabel.CreditNoteDate)),
        creditNoteAmt: Yup.number().required(validationMessages.required(CreditNoteLabel.CreditNoteAmount)),
        eWayBill: Yup.string().required(validationMessages.required(CreditNoteLabel.EWayBill)),
        originalInvoiceDate: Yup.date().required(validationMessages.required(CreditNoteLabel.OriginalInvoiceDate)),
        originalInvoiceNo: Yup.string().required(validationMessages.required(CreditNoteLabel.OriginalInvoiceNumber)),
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: formInitialValue,
        validationSchema,
        onSubmit: (values) => {
            handleFormSubmit(values);
        },
    });

    const handleFormSubmit = (values: any) => {
        setLoader(true);
        const payload = {
            tax_invoice_id: values.taxInvoiceId,
            customer_name: values.vendorName,
            customer_id: Number(values?.vendorId),
            original_invoice_number: values.originalInvoiceNo,
            original_invoice_date: values.originalInvoiceDate,
            credit_note_date: values.creditNoteDate,
            credit_note_amount: values.creditNoteAmt,
            e_way_bill: values.eWayBill,
            reason: values.reason,
        }
        if (creditNoteId) {
            editCreditNote(Number(creditNoteId), payload)
                .then((res) => {
                    if (checkStatusCodeSuccess(res?.statusCode)) {
                        toast.success(res?.message);
                        navigate("/credit-note");
                    } else {
                        toast.error(res?.message);
                    }
                })
                .catch((err) => {
                    errorHandler(err);
                })
                .finally(() => {
                    setLoader(false);
                });
        } else {
            addCreditNote(payload)
                .then((res) => {
                    if (checkStatusCodeSuccess(res?.statusCode)) {
                        toast.success(res?.message);
                        navigate("/credit-note");
                    } else {
                        toast.error(res?.message);
                    }
                })
                .catch((err) => {
                    errorHandler(err);
                })
                .finally(() => {
                    setLoader(false);
                });
        }
    };

    const fetchVendor = () => {
        setLoading(true);
        let data;
        getCustomerList({})
            .then((resp) => {
                if (checkStatusCodeSuccess(resp?.statusCode)) {
                    data = resp?.data;
                    setVendors(
                        data?.map((item: any) => ({
                            value: item?.name,
                            label: item?.name,
                            id: item?.id,
                            vendorId: item?.user_draft_id
                        }))
                    );
                } else {
                    toast.error(resp?.message);
                }
            })
            .catch((err) => {
                return err;
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const fetchVendorData = (id: number) => {
        setLoading(true);
        viewCreditNote(id)
            .then((res) => {
                if (checkStatusCodeSuccess(res?.statusCode)) {
                    setCreditNoteData(res?.data);
                } else {
                    setCreditNoteData([]);
                }
            })
            .catch((err) => {
                setCreditNoteData([]);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const fetchTaxInvoice = () => {
      setLoading(true);
      let data;
      taxInvoiceDropdownApi()
        .then((resp) => {
          if (checkStatusCodeSuccess(resp?.statusCode)) {
            data = resp?.data;
            setTaxInvoice(
              data?.map((item: any) => ({
                value: item?.id,
                label: item?.tax_in_no,
              }))
            );
          } else {
            toast.error(resp?.message);
          }
        })
        .catch((err) => {
          return err;
        })
        .finally(() => {
          setLoading(false);
        });
    };

    const fetchTaxInvoiceData = (id: number) => {
      setLoading(true);
      taxInvoiceDataApi(id)
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            setTaxInvoiceData(res?.data);
            formik.setValues({
                ...formik.values,
                workOrderId: res?.data?.workOrder?.work_order_number || null,
                quotationId: res?.data?.workOrder?.quotation?.quotation_number || null,
            });
          }
        })
        .catch((err) => {
            return err;
        })
        .finally(() => {
          setLoading(false);
        });
    };

    useEffect(() => {
        Promise.all([
            fetchVendor(),
            fetchTaxInvoice(),
        ]);
        if (creditNoteId) {
            fetchVendorData(Number(creditNoteId));
        }
    }, [])

    useEffect(() => {
        if (formik.values.taxInvoiceId) {
            fetchTaxInvoiceData(Number(formik.values.taxInvoiceId));
        }
    }, [formik.values.taxInvoiceId]);

    return (
        <>
            <div className="page-content">
                {loading && <Loader />}
                <div className="flex-grow-1 mx-3">
                    <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
                        <span className="fs-4">
                            {creditNoteId
                                ? `${CreditNoteLabel.EditCreditNote}-${creditNoteData?.credit_note_no || ""}`
                                : CreditNoteLabel.NewCreditNote}
                        </span>
                        <button
                            className="btn btn-danger"
                            onClick={() => navigate("/credit-note")}
                        >
                            <i className="mdi mdi-arrow-left" />
                        </button>
                    </div>
                    <div className="card word-wrap">
                        <div className="card-body">
                            <form>
                                <div className="row">
                                    <div className="col-md-3 col-sm-6 col-12">
                                        <BaseSelect
                                            name="taxInvoiceId"
                                            label={TaxInvoiceLabel.TaxInvoiceNo}
                                            options={taxInvoice}
                                            placeholder={SelectPlaceHolder(TaxInvoiceLabel.TaxInvoiceNo)}
                                            value={formik.values.taxInvoiceId}
                                            handleChange={(field: any, value: any) => {
                                                formik.setFieldValue(field, value);
                                            }}
                                            handleBlur={formik.handleBlur}
                                            error={formik.errors.taxInvoiceId}
                                            touched={formik.touched.taxInvoiceId}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-3 col-sm-6 col-12">
                                        <BaseInput
                                            name="workOrderId"
                                            label={WorkOrderLabel.WorkOrderNo}
                                            placeholder={InputPlaceHolder(WorkOrderLabel.WorkOrderNo)}
                                            value={formik.values.workOrderId}
                                            onChange={formik.handleChange}
                                            handleBlur={formik.handleBlur}
                                            error={formik.errors.workOrderId}
                                            touched={formik.touched.workOrderId}
                                            required
                                            disabled
                                        />
                                    </div>
                                    <div className="col-md-3 col-sm-6 col-12">
                                        <BaseInput
                                            name="quotationId"
                                            label={WorkOrderLabel.QuoteNo}
                                            placeholder={InputPlaceHolder(WorkOrderLabel.QuoteNo)}
                                            value={formik.values.quotationId}
                                            onChange={formik.handleChange}
                                            handleBlur={formik.handleBlur}
                                            error={formik.errors.quotationId}
                                            touched={formik.touched.quotationId}
                                            required
                                            disabled
                                        />
                                    </div>
                                    <div className="col-md-3 col-sm-6 col-12">
                                        <BaseSelect
                                            name="vendorName"
                                            label={WorkOrderLabel.VendorName}
                                            options={vendors}
                                            placeholder={SelectPlaceHolder(WorkOrderLabel.VendorName)}
                                            value={formik.values.vendorName}
                                            handleChange={(field: any, value: any) => {
                                                formik.setFieldValue(field, value);
                                                const selectedVendor: any = vendors.find((vendor: any) => vendor.value === value);
                                                formik.setFieldValue("vendorId", selectedVendor?.id || "");
                                            }}
                                            handleBlur={formik.handleBlur}
                                            error={formik.errors.vendorName}
                                            touched={formik.touched.vendorName}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-3 col-sm-6 col-12">
                                        <BaseInput
                                            name="vendorId"
                                            label={WorkOrderLabel.VendorId}
                                            placeholder={InputPlaceHolder(WorkOrderLabel.VendorId)}
                                            value={(() => {
                                                const selectedVendor: any = vendors.find((vendor: any) => vendor.value === formik.values.vendorName);
                                                return selectedVendor?.vendorId || "";
                                            })()}
                                            onChange={formik.handleChange}
                                            handleBlur={formik.handleBlur}
                                            error={formik.errors.vendorId}
                                            touched={formik.touched.vendorId}
                                            disabled
                                            required
                                        />
                                    </div>
                                    <div className="col-md-3 col-sm-6 col-12">
                                        <BaseInput
                                            name="originalInvoiceNo"
                                            label={CreditNoteLabel?.OriginalInvoiceNumber}
                                            type="text"
                                            placeholder={InputPlaceHolder(
                                                CreditNoteLabel.OriginalInvoiceNumber
                                            )}
                                            value={formik.values.originalInvoiceNo}
                                            onChange={formik.handleChange}
                                            handleBlur={formik.handleBlur}
                                            error={formik.errors.originalInvoiceNo}
                                            touched={formik.touched.originalInvoiceNo}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-3 col-sm-6 col-12">
                                        <BaseInput
                                            name="originalInvoiceDate"
                                            label={CreditNoteLabel?.OriginalInvoiceDate}
                                            type="date"
                                            placeholder={InputPlaceHolder(
                                                CreditNoteLabel.OriginalInvoiceDate
                                            )}
                                            value={formik.values.originalInvoiceDate}
                                            onChange={formik.handleChange}
                                            handleBlur={formik.handleBlur}
                                            error={formik.errors.originalInvoiceDate}
                                            touched={formik.touched.originalInvoiceDate}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-3 col-sm-6 col-12">
                                        <BaseInput
                                            name="creditNoteDate"
                                            label={CreditNoteLabel?.CreditNoteDate}
                                            type="date"
                                            placeholder={InputPlaceHolder(CreditNoteLabel?.CreditNoteDate)}
                                            value={formik.values.creditNoteDate}
                                            onChange={formik.handleChange}
                                            handleBlur={formik.handleBlur}
                                            error={formik.errors.creditNoteDate}
                                            touched={formik.touched.creditNoteDate}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="row mt-2">
                                    <div className="col-md-3 col-sm-6 col-12">
                                        <BaseInput
                                            name="creditNoteAmt"
                                            type="number"
                                            label={CreditNoteLabel?.CreditNoteAmount}
                                            placeholder={InputPlaceHolder(CreditNoteLabel?.CreditNoteAmount)}
                                            value={formik.values.creditNoteAmt}
                                            onChange={formik.handleChange}
                                            handleBlur={formik.handleBlur}
                                            error={formik.errors.creditNoteAmt}
                                            touched={formik.touched.creditNoteAmt}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-3 col-sm-6 col-12">
                                        <BaseInput
                                            name="eWayBill"
                                            label={CreditNoteLabel?.EWayBill}
                                            type="textarea"
                                            placeholder={InputPlaceHolder(CreditNoteLabel?.EWayBill)}
                                            value={formik.values.eWayBill}
                                            onChange={formik.handleChange}
                                            handleBlur={formik.handleBlur}
                                            error={formik.errors.eWayBill}
                                            touched={formik.touched.eWayBill}
                                            rows={1}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-3 col-sm-6 col-12">
                                        <BaseInput
                                            name="reason"
                                            label={CreditNoteLabel.Reason}
                                            type="textarea"
                                            placeholder={InputPlaceHolder(CreditNoteLabel.Reason)}
                                            value={formik.values.reason}
                                            onChange={formik.handleChange}
                                            handleBlur={formik.handleBlur}
                                            error={formik.errors.reason}
                                            touched={formik.touched.reason}
                                            rows={1}
                                        />
                                    </div>
                                </div>
                                <div className="row mt-3 d-flex justify-content-end">
                                    <div className="col-auto d-flex justify-content-end gap-2 text-end">
                                        <BaseButton
                                            type="button"
                                            label={creditNoteId ? updateLabel : submitLabel}
                                            color="success"
                                            loader={loader}
                                            disable={loader}
                                            onClick={() => formik.handleSubmit()}
                                        />
                                        <BaseButton
                                            type="button"
                                            label={cancelLabel}
                                            onClick={() => navigate("/credit-note")}
                                            color="danger"
                                        />
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div >
            </div >
        </>
    );
};

export default CreditNoteForm;
